import { g as getRoomType } from './get-room-type.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Netaffinity New Version**
 *
 * @exports bs_375
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    const isKilleavyCastleEstate = thn.data.accountUserId === 1035894;
    const isWoodfordDolmenHotel = thn.data.propertyId === 1025363;
    const pageChangeUpdate = thn.debounce(() => {
        thn.pageCheck();
        thn.languageCheck();
        if (thn.data.pageName === 'Rooms and Rates') {
            thn.widgets.price.refresh();
        }
    }, 800);
    const forcePageCheckUsers = [1039568].includes(thn.data.account_user_id);
    const debouncedPageCheck = thn.debounce(() => { thn.pageCheck(); }, 500);
    /**
       * @memberOf module:bs_375
       * @see {@link onHashChange}
       *
       * @param {object} ctx - The context object.
       */
    engine.onHashChange = function onHashChange(ctx) {
        if (forcePageCheckUsers && thn.data.page_name === 'Rooms and Rates') {
            thn.pageCheck({ force: true });
        }
        else {
            debouncedPageCheck();
        }
    };
    engine.onStateChange = debouncedPageCheck;
    /**
       * @memberOf module:bs_375
       * @see {@link prestart}
       *
       * @param {object} ctx - The context object.
       */
    engine.boot = function boot(ctx) {
        const container = document.documentElement;
        if (container) {
            const undelegateClickContinue = thn.delegate('click', '.btn-continue', () => {
                thn.widgets.note.destroy();
                thn.widgets.price.hide();
            });
            const handler = function (mutations) {
                const mutationLangChange = [...mutations].map((mutation) => mutation.type === 'childList' && (mutation.target?.classList?.contains('swiper-wrapper')
                    || (typeof mutation.target?.id === 'string' && mutation.target?.id?.includes('batBeacon')))).filter(Boolean);
                if (mutationLangChange.length) {
                    pageChangeUpdate();
                }
            };
            thn.observeDom(container, handler, {
                childList: true,
                subtree: true,
            });
            thn.shutdown(() => {
                pageChangeUpdate.cancel();
                thn.stopObserveDom(container, handler);
                undelegateClickContinue();
            });
        }
        const menuContainer = document.querySelector('#na-menu-bar');
        if (menuContainer) {
            thn.observeDom(menuContainer, pageChangeUpdate, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(() => {
                pageChangeUpdate.cancel();
                thn.stopObserveDom(menuContainer, pageChangeUpdate);
            });
        }
    };
    /**
       * @memberOf module:bs_375
       * @see {@link language_check}
       *
       * @param {object} ctx - The context object.
       */
    engine.languageCheck = function languageCheck(ctx) {
        return new Promise(function (resolve, reject) {
            const language = document.querySelector('.language .ng-binding')?.textContent;
            if (language && ctx.langs.test(language)) {
                resolve(language);
                return;
            }
            reject('no_language');
        });
    };
    /**
     * @memberOf module:bs_375
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
    engine.generateSearchUrl = function generateSearchUrl(ctx) {
        // https://www.killasheehotel.com/bookings.html#!/accommodation/search/date/2023-11-14/2023-11-16
        // https://www.motel.co.uk/bookings/?#!/accommodation/search/date/2023-10-27/2023-10-28
        const url = new URL(ctx.baseUrl);
        const validUrl = url.pathname.includes('bookings');
        if (!validUrl) {
            ctx.newUrl = '';
            return ctx;
        }
        ctx.newUrl = url.toString();
        return ctx;
    };
    /**
    * @memberOf module:bs_375
    * @this thn.widgets.price
    * @see {@link Price#set_data}
    *
    * @param {object} ctx - The context object.
    */
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        let nights;
        // Dates
        const datesElement = window.location.hash;
        const dateBegin = datesElement.split('/')?.slice(4, 5)?.toString()?.replaceAll('-', '/');
        const dateEnd = datesElement.split('/')?.slice(-1)?.toString()?.split('?')[0]?.replaceAll('-', '/');
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
            nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        }
        // Guests, Adults & Children
        const roomInfo = {
            adults: 2,
            children: 0,
            childrenAges: [],
        };
        const guestsEl = document.querySelector('.Guests.active');
        const filterActive = document.querySelector('.Filter.active');
        const isFlexiSearch = !!window.location.hash.match(/flexiSearch/);
        const promoActive = thn.parseUrlParameter('code');
        // If the guest or filters is active the priceSpecial is 1.
        if (promoActive) {
            widgetData.priceSpecial = 2;
        }
        else if (guestsEl || filterActive || isFlexiSearch) {
            widgetData.priceSpecial = 1;
        }
        if (guestsEl) {
            const guestsParamList = thn.parseUrlParameter('occ')?.split(',').filter(Boolean);
            const adultsRaw = guestsParamList.filter((guest) => guest.includes('10x')).map((guest) => guest.replace(/\d+x/, ''));
            const extraAdultsRaw = guestsParamList.filter((guest) => guest.includes('15x')).map((guest) => guest.replace(/\d+x/, ''));
            const childrenRaw = guestsParamList.filter((guest) => guest.match(/^5x|^14x|^19x/)).map((guest) => guest.replace(/\d+x/, ''));
            const infantsRaw = guestsParamList.filter((guest) => guest.includes('7x')).map((guest) => guest.replace(/\d+x/, ''));
            const adults = thn.parseNumber(adultsRaw[0]) || 2;
            const extraAdults = thn.parseNumber(extraAdultsRaw[0]) || 0;
            const children = thn.parseNumber(childrenRaw[0]) || 0;
            const infants = thn.parseNumber(infantsRaw[0]) || 0;
            const totalChildren = children + infants;
            roomInfo.adults = adults + extraAdults;
            roomInfo.children = totalChildren;
            for (let i = 1; i <= totalChildren; i += 1) {
                roomInfo.childrenAges.push(i <= totalChildren - infants ? 10 : 1);
            }
        }
        widgetData.roomsDispo = [roomInfo];
        // Prices
        const roomsArray = [...document.querySelectorAll('.product')];
        // Define a function to check if a product title matches a specific pattern
        function titleMatchesPattern(product, pattern) {
            return product.querySelector('.title')?.textContent.match(pattern);
        }
        let roomsFilter;
        if (isWoodfordDolmenHotel) {
            roomsFilter = (product) => !titleMatchesPattern(product, /Corporate Bed and Breakfast/);
        }
        else if (isKilleavyCastleEstate) { // Killeavy Castle Estate Hotel wants to consider only Bed & Breakfast rates for the price comparison.
            roomsFilter = (product) => titleMatchesPattern(product, /Hotel: Bed and breakfast/);
        }
        else {
            roomsFilter = Boolean; // Keep all products
        }
        const pricesMap = (rate) => {
            const priceEl = rate.querySelector('.price span.ng-binding,.price__number');
            const currencyEl = document.querySelector('.currency span') || thn.parseUrlParameter('curr') || priceEl;
            const price = thn.parseNumber(priceEl?.textContent);
            const currency = thn.parseCurrency(currencyEl?.textContent);
            return {
                price: price,
                currency: currency,
            };
        };
        const pricesArray = roomsArray.filter(roomsFilter).map(pricesMap);
        // Filter the pricesArray to exclude any items where the price or currency property is undefined or null.
        const pricesFilter = (price) => Boolean(price.price) && Boolean(price.currency);
        const prices = pricesArray.filter(pricesFilter).sort((a, b) => a.price - b.price);
        // Currency
        const isUserShowingTotalPrice = [1033901, 1039568, 1045565].includes(thn.data.accountUserId);
        // No available rooms
        const noRooms = document.querySelector('.na-noresults__header');
        if (!thn.empty(prices)) {
            const lowestPrice = prices[0].price;
            widgetData.price = ((isUserShowingTotalPrice || !isFlexiSearch) && nights) ? lowestPrice / nights : lowestPrice;
            widgetData.currency = prices[0].currency;
            widgetData.roomDetails = engine._roomDetailsParseData();
            widgetData.set = true;
        }
        else if (noRooms) {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_375
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const container = document.querySelector('#col-center');
        if (container) {
            let refreshPrice = thn.debounce(() => {
                const rooms = thn.isVisible(document.querySelector('naf-accommodation-search'));
                if (rooms) {
                    ctx.widget.refresh();
                }
            }, 600);
            thn.observeDom(container, refreshPrice, {
                subtree: true,
                childList: true,
                attributes: true,
                characterData: true,
            });
            thn.pageUnload(() => {
                refreshPrice.cancel();
                thn.stopObserveDom(container, refreshPrice);
                refreshPrice = null;
            });
        }
    };
    /**
      *
      * @memberOf module:bs_375
      *
      */
    engine._roomDetailsParseData = function roomDetailsParseData() {
        const roomsInfo = thn.dataLayerHelper.get('products');
        if (!roomsInfo) {
            return undefined;
        }
        const ratesArray = [];
        roomsInfo.forEach((rate) => {
            const exists = ratesArray.some((tariff) => tariff.id === rate.inventoryId);
            if (!exists) {
                const isRefundable = rate.tariffName?.includes('Flexible');
                const tariffInfo = {
                    id: rate.tariffId,
                    name: rate.tariffName,
                    totalNight: rate.price,
                    breakfast: 0, // We don't have breakfast information.
                    refundable: isRefundable ? 1 : 0,
                };
                const rateObject = {
                    id: rate.inventoryId,
                    name: rate.inventoryName,
                    adults: 2,
                    children: 0,
                    type: getRoomType(rate.inventoryName),
                    prices: [tariffInfo],
                };
                ratesArray.push(rateObject);
            }
        });
        return ratesArray;
    };
    /**
    * @memberOf module:bs_375
    * @this thn.widgets.booking
    * @see {@link Booking#set_data}
    *
    * @param {object} ctx - The context object.
    */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        // bookingId
        const bookingId = document.querySelector('.booking-reference span')?.textContent || thn.dataLayerHelper.get('bookingId');
        if (!bookingId) {
            widgetData.set = false;
            return widgetData;
        }
        widgetData.bookingId = bookingId;
        // Dates
        const datesFromDom = document.querySelectorAll('.selected-date li span');
        widgetData.dateBegin = thn.dataLayerHelper.get('startDate')?.replaceAll('-', '/') || (datesFromDom && datesFromDom.length && translateDate(datesFromDom[0]?.textContent, thn.data.language));
        widgetData.dateEnd = thn.dataLayerHelper.get('endDate')?.replaceAll('-', '/') || (datesFromDom && datesFromDom.length && translateDate(datesFromDom[1]?.textContent, thn.data.language));
        // Prices & currency
        const priceEl = document.querySelector('.total')?.nextElementSibling?.textContent;
        widgetData.price = thn.dataLayerHelper.get('values.totalPrice') || thn.parseNumber(priceEl);
        widgetData.currency = thn.dataLayerHelper.get('values.currency') || thn.parseCurrency(priceEl);
        return widgetData;
    };
    /**
     * @memberOf module:bs_375
     * @this thn.widgets.price
     * @see {@link Price#get_promocode}
     *
     * @param {object} ctx - The context object.
     */
    engine.getPromocode = function getPromocode(ctx) {
        const promoCode = thn.parseUrlParameter('code');
        if (promoCode) {
            ctx.promocode = promoCode;
        }
        return ctx.promocode;
    };
    return engine;
}

export { factory };
